import { pdf } from "@react-pdf/renderer";
import { httpClient } from "infra/axios/axios-api";
import { DigitalSignApprover } from "domain/entities/digital-sign-approver";
import { Timesheet } from "domain/entities/timesheet";
import { Tripsheet } from "domain/entities/tripsheet";

import { DigitalSignStepEnum } from "domain/enum/digitalSignSteps";
import { MessageTypeEnum } from "domain/enum/messageType";
import { TaskTypeEnum } from "domain/enum/taskType";
import { TimesheetStatusEnum } from "domain/enum/timesheetStatus";
import { TripsheetStatusEnum } from "domain/enum/tripsheetStatus";
import { DigitalSignInputFilter } from "domain/models/digital-sign-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import TimesheetReportPDF from "presentation/components/Timesheet/TimesheetReportPDF";
import TripsheetReportPDF from "presentation/components/Tripsheet/TripsheetGrid/TripsheetMonthGrid/TripsheetReportPDF";
import {
  useDigitalSignByFilter,
  useUpdateDigitalSignMutation,
} from "presentation/hooks/use-query-digital-sign";
import {
  useCreateDigitalSignApproverMutation,
  useDigitalSignApproverByFilter,
} from "presentation/hooks/use-query-digital-sign-approver";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import {
  useTimesheetsById,
  useUpdateTimesheetMutation,
} from "presentation/hooks/use-query-timesheet";
import { useTimesheetReportsByEmployee } from "presentation/hooks/use-query-timesheet-report";
import {
  useTripsheetById,
  useUpdateTripsheetMutation,
} from "presentation/hooks/use-query-tripsheet";
import { useTripsheetReport } from "presentation/hooks/use-query-tripsheet-report";
import { useExpenseById } from "presentation/hooks/use-query-expense";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import { useEffect, useMemo, useState } from "react";
import { MobileVacationSetting } from "../MobileVacationSettings";
import MobilePDFViewer from "../../MobilePDFViewer";
import MobileExpenseApprovalForm from "../MobileExpenseApprovalForm";

interface Task {
  id: string;
  type: string;
  description: string;
  dateRef?: Date;
  status: string;
}

interface ParamProps {
  //url: string;
  document: Task | undefined;
  onClose(): void;
  showMessage: (type: MessageTypeEnum, subject: string, message: string) => void;
}

const MobileTaskApprovalForm: React.FC<ParamProps> = (params: ParamProps) => {

  const { userEmployee, email: userEmployeeEmail } = useAuthStore();
  const [currentTimesheet, setCurrentTimesheet] = useState<Timesheet>();
  const [currentTripsheet, setCurrentTripsheet] = useState<Tripsheet>();
  const [fileName, setFileName] = useState<string>("");

  const [url, setUrl] = useState("");

  const isTimesheet = useMemo(
    () => params.document?.type === TaskTypeEnum.TIMESHEET,
    [params.document?.type]
  );

  const isTripsheet = useMemo(
    () => params.document?.type === TaskTypeEnum.TRIPSHEET,
    [params.document?.type]
  );

  const isExpense = useMemo(
    () => params.document?.type === TaskTypeEnum.EXPENSE,
    [params.document?.type]
  );


  const { data: timesheetData } = useTimesheetsById(
    isTimesheet ? params.document?.id : undefined
  );
  const { data: tripsheetData } = useTripsheetById(
    isTripsheet ? params.document?.id : undefined
  );
  const { data: expenseData } = useExpenseById(
    isExpense ? params.document?.id : undefined
  );

  const { data: timesheetReportData, refetch: refetchReportTimesheetData } =
    useTimesheetReportsByEmployee(
      isTimesheet ? params.document?.id : undefined,
      {
        sortBy: "id",
        sortOrder: "asc",
        page: 1,
        limit: 100,
      }
    );

  const { data: tripsheetReportData, refetch: refetchReportTripsheetData } =
    useTripsheetReport(isTripsheet ? params.document?.id : undefined, {
      sortBy: "dateTrip",
      page: 1,
      limit: 100,
    });

  const { mutate: updateTimesheet, isSuccess: isSuccessTimesheet, isPending: isUpdatingTimesheet } =
    useUpdateTimesheetMutation();
  const { mutate: updateTripsheet, isPending: isUpdatingTripsheet } = useUpdateTripsheetMutation();

  const { data: digitalSignData, refetch: refetchDigitalSign } =
    useDigitalSignByFilter({
      referenceId: isTripsheet ? params.document?.id : undefined,
    } as DigitalSignInputFilter);

  const { data: digitalSignApprovalData, refetch: refetchDigitalSignApproval } =
    useDigitalSignApproverByFilter({
      referenceId: isTripsheet ? params.document?.id : undefined,
    });

  const {
    mutate: createDigitalSingApprover,
    isPending: isCreatingApproval,
    isSuccess: isSuccessCreateApproval,
    isError: isErrorCreateApproval,
    error: errorCreateApproval,
  } = useCreateDigitalSignApproverMutation();

  const {
    isSuccess: isSuccessUpdateDigitalSign,
    isError: isErrorUpdateDigitalSign,
    error: errorUpdateDigitalSign,
  } = useUpdateDigitalSignMutation();

  const { data: employeeData } = useEmployeesByFilter(
    { email: timesheetReportData?.data?.email ?? "" },
    isTimesheet && !!timesheetReportData?.data?.email
  );

  useEffect(() => {
    if (isTimesheet) {
      setCurrentTimesheet(timesheetData?.data);
      refetchReportTimesheetData();
    } else if (isTripsheet) {
      setCurrentTripsheet(tripsheetData?.data);
      refetchReportTripsheetData();
    }
  }, [
    params.document,
    isTimesheet,
    isTripsheet,
    timesheetData?.data,
    tripsheetData?.data,
    refetchReportTimesheetData,
    refetchReportTripsheetData,
  ]);

  useEffect(() => {
    if (digitalSignData?.data) {
      refetchDigitalSignApproval();
    }
  }, [digitalSignData?.data, refetchDigitalSignApproval]);

  const setFileNameState = (description: string, type: string) => {
    const dateStr = new Date().toLocaleDateString("pt-PT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).replaceAll("/", "-");
    const creator = description.split(" - ")[0].replaceAll(" ", "-");

    setFileName(`${creator}_${type.replaceAll(" ", "-")}_${dateStr}`);
  }

  useEffect(() => {
    const generatePdf = async () => {
      if (!timesheetReportData?.data) {
        showMessage(MessageTypeEnum.ERROR, "Relatório", "Dados não encontrado");
        return;
      }

      try {
        const blob = await pdf(
          <TimesheetReportPDF data={timesheetReportData.data} isSuportService={employeeData?.data?.[0]?.isSuportService ?? false} />
        ).toBlob();
        const url = URL.createObjectURL(blob);
        setUrl(url);
        if (params.document?.description)
          setFileNameState(params.document?.description, params.document?.type);

      } catch (error) {
        showMessage(
          MessageTypeEnum.ERROR,
          "Relatório",
          "Falha ao gerar o ficheiro"
        );
      }
    };

    if (timesheetReportData?.data) {
      generatePdf();
    } else if (params.document?.type.includes("Mapa de Horas")) {
      setUrl("");
      showMessage(
        MessageTypeEnum.ERROR,
        "Relatório",
        "Não há dados para este relatório. Contactar o colaborador."
      );
      setTimeout(() => {
        params.onClose();
      }, 1500); // Reduzido de 3s para 1.5s
    }
  }, [timesheetReportData, employeeData, params]);

  useEffect(() => {
    const generatePdf = async () => {
      if (
        digitalSignApprovalData?.data &&
        params?.document?.status === TripsheetStatusEnum.ASSINADO_1
      ) {
        const digitalSign = digitalSignApprovalData?.data?.filter(
          (item) => item.digitalSign.referenceId === params.document?.id
        );

        const documentApprovedEmployee = digitalSign?.find(
          (item) => item.step === DigitalSignStepEnum.STEP_EMPLOYEE
        );
        let s3Path: string | undefined = "";
        if (documentApprovedEmployee?.documentPath) {
          s3Path = documentApprovedEmployee?.documentPath;
        } else {
          const documentApprovedNimber = digitalSign?.find(
            (item) => item.step === DigitalSignStepEnum.STEP_COMPANY
          );
          s3Path = documentApprovedNimber?.documentPath;
        }

        if (s3Path) {
          const signedUrlResponse = await httpClient.get(
            `/assets/${s3Path}`
          );

          const signedUrl = signedUrlResponse.data.url;
          if (signedUrl) {
            setUrl(signedUrl);
          } else {
            showMessage(
              MessageTypeEnum.ERROR,
              "Relatório",
              "Não foi possível obter URL do relatório."
            );
          }
        }
      }
    };
    if (digitalSignApprovalData?.data) {
      generatePdf();
    } else if (params.document?.type.includes("Mapa de KMs")) {
      setUrl("");
      showMessage(
        MessageTypeEnum.ERROR,
        "Relatório",
        "Não há dados para este relatório."
      );
      setTimeout(() => {
        params.onClose();
      }, 1500); // Reduzido de 3s para 1.5s
    }
  }, [digitalSignApprovalData, params]);

  useEffect(() => {
    const generatePdf = async () => {
      if (!tripsheetReportData?.data) {
        showMessage(MessageTypeEnum.ERROR, "Relatório", "Dados não encontrado");
        return;
      }

      try {
        if (
          tripsheetReportData?.data &&
          params.document?.status === TripsheetStatusEnum.A_ASSINAR
        ) {
          const blob = await pdf(
            <TripsheetReportPDF data={tripsheetReportData?.data} />
          ).toBlob();
          const url = URL.createObjectURL(blob);
          setUrl(url);
          if (params.document?.description)
            setFileNameState(params.document?.description, params.document?.type);
        }
      } catch (error) {
        showMessage(
          MessageTypeEnum.ERROR,
          "Relatório",
          "Falha ao gerar o ficheiro"
        );
      }
    };

    if (tripsheetReportData?.data) {
      generatePdf();
    } else if (params.document?.type.includes("Mapa de KMs")) {
      setUrl("");
      showMessage(
        MessageTypeEnum.ERROR,
        "Relatório",
        "Não há dados para este relatório."
      );
      setTimeout(() => {
        params.onClose();
      }, 1500); // Reduzido de 3s para 1.5s
    }
  }, [tripsheetReportData, params]);

  useEffect(() => {
    if (isSuccessTimesheet && currentTimesheet) {
      if (currentTimesheet.status === TimesheetStatusEnum.APROVADO) {
        showMessage(
          MessageTypeEnum.SUCCESS,
          "Workflow",
          "Documento foi aprovado com sucesso"
        );
      } else if (currentTimesheet.status === TimesheetStatusEnum.EDITADO) {
        showMessage(
          MessageTypeEnum.WARN,
          "Workflow",
          "Documento foi reprovado e retornou ao estado de edição"
        );
      }
      setTimeout(() => {
        params.onClose();
      }, 1500);
    }
  }, [isSuccessTimesheet, currentTimesheet, params]);

  useEffect(() => {
    if (isErrorUpdateDigitalSign) {
      console.error(errorUpdateDigitalSign?.response.data.metadata.errors[0]);
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "Erro ao atualizar a assinatura digital"
      );
    }
  }, [isErrorUpdateDigitalSign, errorUpdateDigitalSign]);

  useEffect(() => {
    if (isErrorCreateApproval) {
      console.log(
        "errorCreateApproval",
        errorCreateApproval?.response.data.metadata.errors[0]
      );
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "Erro ao assinar documento digital"
      );
    }
  }, [isErrorCreateApproval, errorCreateApproval]);

  useEffect(() => {
    if (currentTripsheet?.id) {
      refetchDigitalSign();
    }
  }, [currentTripsheet?.id, refetchDigitalSign]);

  useEffect(() => {
    if (
      isSuccessUpdateDigitalSign &&
      currentTripsheet?.status === TripsheetStatusEnum.REJEITADO
    ) {
      // updateTripsheet(currentTripsheet);
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "Documento foi reprovado e deverá ser atualizado pela equipa de Finanças"
      );
      setTimeout(() => {
        params.onClose();
      }, 1500);
    }
  }, [isSuccessUpdateDigitalSign, currentTripsheet, params]);

  const showMessage = params.showMessage;

  // Stays blocked after success too, while the form waits 1.5s before closing
  const isSigning = isUpdatingTimesheet || isCreatingApproval || isUpdatingTripsheet;
  const hasSigned = isSuccessTimesheet || isSuccessCreateApproval;

  const handleApprove = async () => {
    if (isSigning || hasSigned) return;
    if (params.document?.id && params.document.type) {

      switch (params.document.type) {
        case TaskTypeEnum.TIMESHEET:
          const timesheet = timesheetData?.data;
          if (
            timesheet &&
            timesheet.status === TimesheetStatusEnum.ENVIADO &&
            userEmployeeEmail
          ) {
            timesheet.status = TimesheetStatusEnum.APROVADO;

            timesheet.lastModifiedBy = userEmployeeEmail;
            setCurrentTimesheet({ ...timesheet });
            updateTimesheet(timesheet);
          }

          break;
        case TaskTypeEnum.TRIPSHEET: {
          const tripsheet = tripsheetData?.data;
          const digitalSign = digitalSignData?.data?.find(
            (item) => item.referenceId === tripsheet?.id
          );
          if (!tripsheet || !digitalSign || !userEmployeeEmail || !userEmployee?.id) break;

          // Nimber signs first ("A Assinar" → "Assinado 1"), then the employee ("Assinado 1" → "Assinado 2")
          const isCompanySignature = tripsheet.status === TripsheetStatusEnum.A_ASSINAR;
          const isEmployeeSignature = tripsheet.status === TripsheetStatusEnum.ASSINADO_1;
          if (!isCompanySignature && !isEmployeeSignature) break;

          // New object instead of mutating the cached query data
          const updatedTripsheet = {
            ...tripsheet,
            status: isCompanySignature
              ? TripsheetStatusEnum.ASSINADO_1
              : TripsheetStatusEnum.ASSINADO_2,
            lastModifiedBy: userEmployeeEmail,
          };
          setCurrentTripsheet(updatedTripsheet);

          const signApproval: DigitalSignApprover = {
            employee: {
              id: userEmployee.id,
            },
            digitalSign: digitalSign,
            step: isCompanySignature
              ? DigitalSignStepEnum.STEP_COMPANY
              : DigitalSignStepEnum.STEP_EMPLOYEE,
          };

          // Handled once per click; a useEffect on isSuccess re-ran on every re-render (#936)
          createDigitalSingApprover(signApproval, {
            onSuccess: () => {
              updateTripsheet(updatedTripsheet);
              showMessage(
                MessageTypeEnum.SUCCESS,
                "Workflow",
                "Documento foi aprovado com sucesso"
              );
              setTimeout(() => {
                params.onClose();
              }, 1500);
            },
          });
          break;
        }

        case TaskTypeEnum.VACATION_PLAN:
          break;
      }
    }
  };

  if (
    !tripsheetReportData?.data &&
    !timesheetReportData?.data &&
    !params.document?.type.includes("Férias") &&
    !params.document?.type.includes("Despesa")
  ) {
    return (
      <div>
        <Spinner />
      </div>
    );
  }


  return (
    <div className="h-full px-0">
      {" "}
      {!params.document?.type.includes("Férias") && !params.document?.type.includes("Despesa") && (
        <div className="flex flex-column h-full w-full" >
          <MobilePDFViewer url={url} fileName={fileName} />
          <div style={{ textAlign: "right", marginTop: "20px" }}>
            <Button
              label="Assinar"
              icon="pi pi-check"
              loading={isSigning}
              disabled={isSigning || hasSigned}
              onClick={(e) => {
                e.stopPropagation();
                handleApprove();
              }}
            />
          </div>
        </div>
      )}
      {params.document?.type.includes("Pedido de Férias") && (
        <MobileVacationSetting
          managerId={userEmployee?.id}
          isAdmin={false}
          isManager={true}
          date={params.document.dateRef || new Date()}
          vacationId={params.document?.id}
          onClose={params.onClose}
          showMessage={params.showMessage}
          task="Pedido"
        />
      )}
      {params.document?.type.includes("Pedido de Cancelamento de Férias") && (
        <MobileVacationSetting
          managerId={userEmployee?.id}
          isAdmin={false}
          isManager={true}
          date={params.document.dateRef || new Date()}
          vacationId={params.document?.id}
          onClose={params.onClose}
          showMessage={params.showMessage}
          task="Cancelamento"
        />
      )}

      {params.document?.type.includes("Despesa") && expenseData?.data && (
        <MobileExpenseApprovalForm
          expense={expenseData.data}
          onClose={params.onClose}
          showMessage={params.showMessage}
        />
      )}
    </div>

  );
};

export default MobileTaskApprovalForm;
